import { isNil } from 'lodash';
import React, { useCallback } from 'react';
import { MBMessageBubble } from '@shared/scenes/chat/thread/components/message/bubble';
import { MBMessageBubbleTheme } from '@shared/scenes/chat/thread/components/message/bubble/styles';
import { MBMessageWithMarkersWrapper } from '@shared/scenes/chat/thread/components/message/marker/withMarkers';
import { MBSystemMessage } from '@shared/scenes/chat/thread/components/message/system';
import { MBSystemMessageGroup } from '@shared/scenes/chat/thread/components/message/system/group';
import { useGetChannelEventHandlers } from '@shared/scenes/workflows/hooks';
import { useDispatch, useSelector } from '@shared/store';
import { useCurrentUserId, useIsDesktop } from '@shared/util/hooks';
import * as Actions from '../store/actions';
import { MBChatBanner } from './banner';

// We don't want messages to have to deal with logic to make sure the layout
// prop is set. Therefore we use this defaultLayout if the store has not yet
// been populated with message layouts.
const defaultLayout: MBChatMessageLayoutOptions = {
  isGroupedWithAbove: false,
  isGroupedWithBelow: false,
  isReadMarkerVisible: false,
  isDateMarkerVisible: false,
  isUsersLastSentMessage: false,
  isLastMessage: false,
  seenByUsers: [],
  groupedSystemMessages: null,
  isGroupedSystemMessage: false,
};

interface Props {
  message: MBAPI.ChatMessage;
  channelSlug: string;
  isDM: boolean;
  isSelected?: boolean;
  // TODO(fant): remove when link preview meta is supplied by the backend
  isHidingLinkPreview?: boolean;
  previousMessage?: MBAPI.ChatMessage | null;
}

export function MBMessage({
  message,
  channelSlug,
  isDM,
  isSelected,
  isHidingLinkPreview,
  previousMessage,
}: Props) {
  const currentUserId = useCurrentUserId();
  const isMe = currentUserId === message.sender.id;
  const isEditing = useSelector(
    state => state.chatMessages.messageBeingEditedFromDraftId[channelSlug]?.id === message.id,
  );
  const isDesktop = useIsDesktop();
  const deliveryStatus = useSelector(
    state => state.chatMessages.deliveryStatusFromMessageUuid[message.meta.uuid ?? ''],
  );
  const layout = useSelector(state => state.chatMessages.layoutOptionsFromMessageId[message.id]);
  const getChannelEventHandlers = useGetChannelEventHandlers();
  const openCard = useCallback(
    async (cardId: string) => {
      const { onOpenCard, foundCard } = await getChannelEventHandlers(cardId);
      onOpenCard(foundCard);
    },
    [getChannelEventHandlers],
  );

  const dispatch = useDispatch();
  const resend = useCallback(() => Actions.resend(channelSlug)(dispatch), [channelSlug, dispatch]);
  const deleteMessage = useCallback(
    () => Actions.deleteMessage(message, channelSlug)(dispatch),
    [message, channelSlug, dispatch],
  );
  const editMessage = useCallback(
    () => Actions.startEditingMessage(message, channelSlug)(dispatch),
    [message, channelSlug, dispatch],
  );

  if (!isNil(previousMessage) && previousMessage.createdAt > message.createdAt) {
    return null;
  }

  return (
    <MBMessageDataWrapper
      message={message}
      channelSlug={channelSlug}
      isDM={isDM}
      isSelected={isSelected}
      isHidingLinkPreview={isHidingLinkPreview}
      isMe={isMe}
      isEditing={isEditing}
      isDesktop={isDesktop}
      deliveryStatus={deliveryStatus}
      layout={layout}
      resend={resend}
      deleteMessage={deleteMessage}
      editMessage={editMessage}
      openCard={openCard}
    />
  );
}

interface MBMessageDataWrapperProps extends Props {
  isMe: boolean;
  isEditing: boolean;
  isDesktop: boolean;
  deliveryStatus: MBDeliveryStatus;
  layout: MBChatMessageLayoutOptions;
  resend: VoidFunction;
  deleteMessage: VoidFunction;
  editMessage: VoidFunction;
  openCard: (cardId: string) => void;
}

/*
    Given a message, render the correct message type and connect it with the
    right layout and delivery status.

    To understand why this is a PureComponent and not an SFC, see MBChatThread.
    It's ok to connect the component to the store, given that we alwas get the
    same instances of objects from the store (so that shallow diffing works).
*/
export const MBMessageDataWrapper = React.memo(function MBMessageDataWrapper(
  props: MBMessageDataWrapperProps,
) {
  const {
    isMe,
    isDM,
    isEditing,
    isSelected,
    message,
    deliveryStatus,
    resend,
    deleteMessage,
    editMessage,
    channelSlug,
    openCard,
    isHidingLinkPreview,
    isDesktop,
    layout: layoutFromProps,
  } = props;

  const layout = layoutFromProps ?? defaultLayout;
  const anchorName = `--seen-by-${message.id}`;

  let content: React.ReactNode = null;

  if (message.type === 'SYSTEM' && message.subType === 'CHANNEL_BANNER') {
    content = <MBChatBanner channelSlug={channelSlug} creator={message.sender} />;
  } else if (message.type === 'SYSTEM') {
    if (layout.isGroupedSystemMessage) {
      if (!layout.groupedSystemMessages) {
        content = null;
      } else {
        content = (
          <MBSystemMessageGroup
            isDesktop={isDesktop}
            anchorName={anchorName}
            messages={layout.groupedSystemMessages}
            layout={layout}
          />
        );
      }
    } else {
      content = (
        <MBSystemMessage
          message={message}
          layout={layout}
          isDM={isDM}
          isDesktop={isDesktop}
          channelSlug={channelSlug}
          onOpenCard={openCard}
        />
      );
    }
  } else {
    const theme = MBMessageBubbleTheme.get({
      me: isMe,
      dm: isDM,
      selected: !!isSelected,
      editing: isEditing,
    });

    if (!isNil(theme)) {
      if (isMe) {
        const isResendable = ['waiting', 'failed'].includes(deliveryStatus);
        content = (
          <MBMessageBubble
            theme={theme}
            layout={layout}
            message={message}
            onPress={isResendable ? resend : undefined}
            onDelete={deleteMessage}
            onEdit={editMessage}
            deliveryStatus={layout.isUsersLastSentMessage ? 'sent' : deliveryStatus}
            channelSlug={channelSlug}
            isDesktop={isDesktop}
            isHidingLinkPreview={isHidingLinkPreview}
          />
        );
      } else {
        content = (
          <MBMessageBubble
            theme={theme}
            layout={layout}
            message={message}
            channelSlug={channelSlug}
            isDesktop={isDesktop}
            isHidingLinkPreview={isHidingLinkPreview}
          />
        );
      }
    }
  }

  return (
    <MBMessageWithMarkersWrapper layout={layoutFromProps} message={message}>
      {content}
    </MBMessageWithMarkersWrapper>
  );
});
